import React from 'react';
import { ScheduleConfig, ScheduleFrequency } from '../../../../types/monitoring';
import { TIMEZONES, DAYS_OF_WEEK } from '../../../../constants/scheduling';
import { Button } from '../../../ui/Button';

interface MonitorScheduleEditorProps {
  schedule: ScheduleConfig;
  maxResults: number;
  isUpdating: boolean;
  onScheduleChange: (schedule: ScheduleConfig) => void;
  onMaxResultsChange: (value: number) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const MonitorScheduleEditor: React.FC<MonitorScheduleEditorProps> = ({
  schedule,
  maxResults,
  isUpdating,
  onScheduleChange,
  onMaxResultsChange,
  onSave,
  onCancel,
}) => {
  return (
    <div className="space-y-4 p-4 bg-neutral-50 rounded-xl">
      <div>
        <label id="frequency-label" className="block text-[11px] font-medium text-neutral-500 uppercase tracking-wide mb-2">
          Frequency
        </label>
        <div className="flex gap-2" role="radiogroup" aria-labelledby="frequency-label">
          {(['daily', 'weekly', 'monthly'] as ScheduleFrequency[]).map((freq) => (
            <Button
              key={freq}
              id={`frequency-${freq}`}
              variant={schedule.frequency === freq ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => onScheduleChange({ ...schedule, frequency: freq })}
              role="radio"
              aria-checked={schedule.frequency === freq}
            >
              {freq.charAt(0).toUpperCase() + freq.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {schedule.frequency === 'weekly' && (
        <div>
          <label id="days-label" className="block text-[11px] font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Days
          </label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="days-label">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                id={`day-${day.value}`}
                onClick={() => {
                  const current = schedule.daysOfWeek;
                  const updated = current.includes(day.value)
                    ? current.filter((d) => d !== day.value)
                    : [...current, day.value];
                  onScheduleChange({
                    ...schedule,
                    daysOfWeek: updated.length > 0 ? updated : [day.value],
                  });
                }}
                aria-pressed={schedule.daysOfWeek.includes(day.value)}
                aria-label={day.label}
                className={`w-9 h-9 text-[11px] font-medium rounded-lg transition-colors ${
                  schedule.daysOfWeek.includes(day.value)
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-300'
                }`}
              >
                {day.label.slice(0, 2)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label htmlFor="schedule-time" className="block text-[11px] font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Time
          </label>
          <select
            id="schedule-time"
            value={schedule.hourOfDay}
            onChange={(e) => onScheduleChange({ ...schedule, hourOfDay: parseInt(e.target.value) })}
            aria-label="Select time of day"
            className="w-full h-10 px-3 text-[13px] bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="schedule-timezone" className="block text-[11px] font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Timezone
          </label>
          <select
            id="schedule-timezone"
            value={schedule.timezone}
            onChange={(e) => onScheduleChange({ ...schedule, timezone: e.target.value })}
            aria-label="Select timezone"
            className="w-full h-10 px-3 text-[13px] bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="schedule-max-results" className="block text-[11px] font-medium text-neutral-500 uppercase tracking-wide mb-2">
            Max/run
          </label>
          <input
            id="schedule-max-results"
            type="number"
            value={maxResults}
            onChange={(e) => onMaxResultsChange(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
            min={1}
            max={200}
            aria-label="Maximum results per run"
            className="w-full h-10 px-3 text-[13px] bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button id="cancel-schedule-edit" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button id="save-schedule" variant="primary" size="sm" onClick={onSave} disabled={isUpdating} loading={isUpdating}>
          Save changes
        </Button>
      </div>
    </div>
  );
};
